import React, { useEffect, useRef, useState } from "react";
import { Camera, X, Zap, CheckCircle2 } from "lucide-react";

// Live camera with framing guide, sharpness/brightness checks and auto-capture.
export default function CameraScanner({ onCapture, onClose }) {
  const video = useRef(); const canvas = useRef(); const stream = useRef(null);
  const [msg, setMsg] = useState("Place the entire bill inside the frame");
  const [ok, setOk] = useState(false); const [shots, setShots] = useState([]); const [auto, setAuto] = useState(true);
  const stable = useRef(0); const busy = useRef(false);

  useEffect(() => {
    let live = true;
    navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
      .then(s => { if (!live) return s.getTracks().forEach(t => t.stop()); stream.current = s; video.current.srcObject = s; video.current.play(); })
      .catch(() => setMsg("Camera not available — use Upload instead"));
    return () => { live = false; stream.current?.getTracks().forEach(t => t.stop()); };
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      const vd = video.current; if (!vd || vd.readyState < 2) return;
      const c = canvas.current; const w = 160, h = Math.round(160 * vd.videoHeight / vd.videoWidth) || 120; c.width = w; c.height = h;
      const ctx = c.getContext("2d", { willReadFrequently: true }); ctx.drawImage(vd, 0, 0, w, h);
      const d = ctx.getImageData(0, 0, w, h).data; const g = new Float32Array(w * h); let sum = 0;
      for (let i = 0; i < w * h; i++) { g[i] = (d[i * 4] * 0.299 + d[i * 4 + 1] * 0.587 + d[i * 4 + 2] * 0.114); sum += g[i]; }
      const mean = sum / (w * h); let lap = 0, n = 0;
      for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) { const i = y * w + x; const l = 4 * g[i] - g[i - 1] - g[i + 1] - g[i - w] - g[i + w]; lap += l * l; n++; }
      const sharp = lap / n;
      let m = "", good = false;
      if (mean < 55) m = "Too dark — move to better light"; else if (mean > 225) m = "Too bright / glare — tilt slightly"; else if (sharp < 60) m = "Hold steady — image is blurry"; else { m = "Bill detected — hold steady"; good = true; }
      setMsg(m); setOk(good);
      stable.current = good ? stable.current + 1 : 0;
      if (auto && good && stable.current >= 4 && !busy.current) { stable.current = 0; snap(); }
    }, 350);
    return () => clearInterval(id);
  }, [auto]); // eslint-disable-line

  const snap = () => {
    const vd = video.current; if (!vd || busy.current) return; busy.current = true;
    const c = document.createElement("canvas"); c.width = vd.videoWidth; c.height = vd.videoHeight;
    c.getContext("2d").drawImage(vd, 0, 0);
    c.toBlob(b => { const f = new File([b], `scan-${Date.now()}.jpg`, { type: "image/jpeg" }); setShots(s => [...s, { f, url: URL.createObjectURL(b) }]); if (navigator.vibrate) navigator.vibrate(40); setTimeout(() => { busy.current = false; }, 1500); }, "image/jpeg", 0.92);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black text-white flex flex-col" data-testid="camera-scanner">
      <div className="flex items-center justify-between px-4 py-3">
        <button onClick={onClose} className="p-2" data-testid="camera-close"><X size={20} /></button>
        <div className="text-[11px] tracking-[0.18em] uppercase font-semibold">Scan bill</div>
        <button onClick={() => setAuto(a => !a)} className={`text-[10px] px-2 py-1 rounded-full border ${auto ? "bg-white text-black border-white" : "border-white/40"}`} data-testid="camera-auto"><Zap size={11} className="inline mr-1" />Auto</button>
      </div>
      <div className="relative flex-1 overflow-hidden">
        <video ref={video} playsInline muted className="absolute inset-0 h-full w-full object-cover" />
        <canvas ref={canvas} className="hidden" />
        <div className="absolute inset-0 pointer-events-none">
          <div className={`absolute inset-x-[8%] inset-y-[10%] rounded-xl border-2 transition-colors ${ok ? "border-emerald-400" : "border-white/70"}`}>
            {["top-0 left-0 border-t-4 border-l-4", "top-0 right-0 border-t-4 border-r-4", "bottom-0 left-0 border-b-4 border-l-4", "bottom-0 right-0 border-b-4 border-r-4"].map(c => <span key={c} className={`absolute h-7 w-7 ${c} ${ok ? "border-emerald-400" : "border-white"} rounded-sm`} />)}
          </div>
          <div className={`absolute left-1/2 -translate-x-1/2 bottom-6 px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur ${ok ? "bg-emerald-500/90" : "bg-black/60"}`} data-testid="camera-hint">{msg}</div>
        </div>
      </div>
      <div className="px-4 py-4 flex items-center justify-between gap-3" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
        <div className="flex -space-x-2 min-w-[72px]">{shots.slice(-3).map((s, i) => <img key={i} src={s.url} alt="" className="h-12 w-9 object-cover rounded border border-white/60" />)}</div>
        <button onClick={snap} aria-label="Capture" data-testid="camera-capture" className="h-16 w-16 rounded-full bg-white grid place-items-center ring-4 ring-white/30 active:scale-95"><Camera size={26} className="text-black" /></button>
        <button disabled={!shots.length} onClick={() => onCapture(shots.map(s => s.f))} data-testid="camera-done" className="min-w-[72px] inline-flex items-center justify-center gap-1 text-xs font-semibold px-3 py-2 rounded-full bg-emerald-500 disabled:opacity-30"><CheckCircle2 size={14} /> {shots.length ? `Use ${shots.length}` : "Done"}</button>
      </div>
    </div>
  );
}
